import { useState } from "react";
import { useCart } from "../store/cart";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    shipping_address: "",
    city: "",
    postal_code: "",
    country: "US",
  });

  const shipping = subtotal >= 100 || subtotal === 0 ? 0 : 8;
  const total = subtotal + shipping;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const checkout = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("EMPTY BAG. NOTHING TO RIDE FOR."); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.toLowerCase(),
        items: items.map((it) => ({ product_id: it.product_id, size: it.size, quantity: it.quantity })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      // Save order id for potential lookup
      localStorage.setItem("ts_last_order_id", data.order_id);
      localStorage.setItem("ts_last_order_email", payload.email);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cart-page" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <p className="text-blood text-xs tracking-[0.3em] font-mono mb-2">// CHECKOUT</p>
      <h1 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter">
        YOUR <span className="text-blood">BAG</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 border border-white/15">
          {items.length === 0 && (
            <div className="p-12 text-center">
              <p className="font-marker text-blood text-3xl">bag is empty.</p>
              <Link to="/shop" data-testid="empty-shop-link" className="inline-block mt-6 px-6 py-3 bg-bone text-ink text-xs tracking-[0.3em] uppercase hover:bg-blood hover:text-bone">
                FIND SOMETHING TO RIOT IN →
              </Link>
            </div>
          )}
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-row-${it.product_id}`} className="flex gap-4 p-5 border-b border-white/15 last:border-b-0">
              <img src={it.image} alt={it.name} className="w-24 h-28 object-cover border border-white/10" />
              <div className="flex-1 min-w-0">
                <h3 className="font-anton uppercase text-bone tracking-tight text-lg">{it.name}</h3>
                <p className="text-[11px] tracking-[0.2em] uppercase text-ash mt-1">SIZE / {it.size}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button data-testid={`cart-dec-${it.key}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="w-8 h-8 border border-white/20 text-bone hover:bg-blood">
                    <Minus size={14} className="m-auto" />
                  </button>
                  <span className="w-8 text-center font-mono text-bone">{it.quantity}</span>
                  <button data-testid={`cart-inc-${it.key}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="w-8 h-8 border border-white/20 text-bone hover:bg-blood">
                    <Plus size={14} className="m-auto" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <span className="font-mono text-bone">${(it.price * it.quantity).toFixed(2)}</span>
                <button data-testid={`cart-remove-${it.key}`} onClick={() => removeItem(it.key)} className="text-ash hover:text-blood">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <form data-testid="checkout-form" onSubmit={checkout} className="border border-white/15 p-6 space-y-4 h-fit sticky top-24">
          <h2 className="font-anton uppercase text-bone text-2xl tracking-tighter">SHIPPING + PAYMENT</h2>
          <input data-testid="checkout-email" name="email" type="email" required value={form.email} onChange={onChange} placeholder="EMAIL" className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
          <input data-testid="checkout-name" name="full_name" required value={form.full_name} onChange={onChange} placeholder="FULL NAME" className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
          <input data-testid="checkout-address" name="shipping_address" required value={form.shipping_address} onChange={onChange} placeholder="STREET ADDRESS" className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="checkout-city" name="city" required value={form.city} onChange={onChange} placeholder="CITY" className="bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <input data-testid="checkout-postal" name="postal_code" required value={form.postal_code} onChange={onChange} placeholder="POSTAL" className="bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
          </div>
          <input data-testid="checkout-country" name="country" required value={form.country} onChange={onChange} placeholder="COUNTRY" className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />

          <div className="border-t border-white/15 pt-4 space-y-1 text-sm font-mono text-bone">
            <div className="flex justify-between"><span>SUBTOTAL</span><span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-ash text-xs tracking-[0.2em]"><span>SHIPPING</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between text-lg font-anton mt-2 pt-2 border-t border-white/15"><span>TOTAL</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
          </div>

          <button
            data-testid="checkout-submit-button"
            disabled={submitting || items.length === 0}
            type="submit"
            className="w-full px-6 py-4 bg-blood text-bone text-xs tracking-[0.3em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "REDIRECTING..." : "PAY NOW →"}
          </button>
          <p className="text-[10px] tracking-[0.2em] text-ash text-center">SECURED BY STRIPE. NO REFUNDS. NO MERCY.</p>
        </form>
      </div>
    </div>
  );
}
